import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildHelpMarkdown, createCommandSet, isAllowedChatModel } from "@attmos/ruby-core";
import {
  ensureRubyTheme,
  RubyEmptyState,
  RubyHeader,
  RubyHistoryDrawer,
  RubyInput,
  type RubyInputHandle,
  RubyMessageList,
  useLlamaStatus,
  useRubyChat,
  useRubyPlatform,
} from "@attmos/ruby-ui";

import { useRubyPanelStore } from "@/stores/ruby-panel";

/** Markdown-escape text echoed back into a local assistant message. */
function escapeMarkdown(text: string): string {
  return text.replace(/([\\`*_{}[\]()#+\-!<>|~])/g, "\\$1");
}

function modelPickerMarkdown(
  currentId: string,
  chatModels: ReadonlyArray<{ id: string; shortName: string; description: string }>
): string {
  return [
    `**Current model:** \`${escapeMarkdown(currentId)}\``,
    "",
    "Pick one with `/model <id>`:",
    "",
    ...chatModels.map(
      (m) =>
        `- \`${escapeMarkdown(m.id)}\` — ${escapeMarkdown(m.shortName)} · ${escapeMarkdown(m.description)}`
    ),
  ].join("\n");
}

/**
 * The Ruby assistant docked beside the rail (fs#2534) — FlowStack's only
 * Ruby surface. The rail's Ruby row opens it; ruby-ui's floating
 * `RubyWidget` is not mounted anywhere (fs#2540).
 *
 * The chat is composed here from ruby-ui's own pieces (`useRubyChat`,
 * `RubyHeader`, `RubyMessageList`, `RubyInput`, `RubyHistoryDrawer`,
 * `RubyEmptyState`) under the `RubyPlatformProvider` AppShell mounts, laid
 * out in normal flow, filling the left pane of AppShell's split. ruby-ui's
 * `RubyPanel` is `position: fixed` in the bottom-right corner, which is why
 * it is not used.
 *
 * Mirrors `RubyPanel`'s behaviour for what FlowStack's adapter turns on
 * (streaming, history, new chat, the slash commands, model and thinking
 * toggles). Receipt upload, the financial-context pill and ruby-ui's
 * minimize/resize corner are not FlowStack features or have no meaning in a
 * docked pane, so they are left out. The header's Minimize and Close both
 * close the panel: there is no smaller docked form.
 *
 * Renders nothing when the adapter denies `chat:use` or hides Ruby on the
 * route — the same gates ruby-ui's `RubyWidget` applies.
 */
export function RubyDockedPanel({ currentPath }: { currentPath: string }): JSX.Element | null {
  const adapter = useRubyPlatform();
  if (adapter.hasPermission && !adapter.hasPermission("chat:use")) return null;
  if (adapter.shouldHideOnRoute?.(currentPath)) return null;
  return <RubyDockedPanelInner currentPath={currentPath} />;
}

function RubyDockedPanelInner({ currentPath }: { currentPath: string }): JSX.Element {
  const adapter = useRubyPlatform();
  const userId = adapter.getUserId() ?? "anonymous";
  const chat = useRubyChat(currentPath);
  const { status } = useLlamaStatus(true);
  const closePanel = useRubyPanelStore((s) => s.close);
  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);

  const inputRef = useRef<RubyInputHandle>(null);

  useEffect(() => {
    ensureRubyTheme();
    // Opened by a click just now: the composer is where the user is
    // headed, the same place ruby-ui's own `RubyPanel` puts focus. A panel
    // restored open by a reload does not take focus from the page.
    if (useRubyPanelStore.getState().consumeFocusRequest()) inputRef.current?.focus();
  }, []);

  const commands = useMemo(
    () => createCommandSet(adapter.slashCommandExtensions),
    [adapter.slashCommandExtensions]
  );

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    void chat.sendMessage(text);
  }, [chat, input]);

  const sendPrompt = useCallback(
    (prompt: string) => {
      setInput("");
      void chat.sendMessage(prompt);
    },
    [chat]
  );

  const showModels = useCallback(
    () => chat.appendLocalAssistantMessage(modelPickerMarkdown(chat.model, adapter.chatModels)),
    [chat, adapter.chatModels]
  );

  const handleSlashCommand = useCallback(
    (cmd: string, args: string) => {
      switch (cmd) {
        case "/new":
          chat.newChat();
          setHistoryOpen(false);
          return;
        case "/clear":
          if (chat.activeSessionId) {
            void chat.deleteSession(chat.activeSessionId, { skipConfirm: true });
          } else {
            chat.newChat();
          }
          setHistoryOpen(false);
          return;
        case "/search":
          setHistoryOpen(true);
          return;
        case "/model":
          if (!args) {
            showModels();
          } else if (!isAllowedChatModel(args, adapter.chatModels)) {
            chat.appendLocalAssistantMessage(
              `Unknown model: \`${escapeMarkdown(args)}\`. Run \`/model\` to see the available list.`
            );
          } else if (chat.setModel(args)) {
            chat.appendLocalAssistantMessage(
              `Got it — future messages will use **\`${escapeMarkdown(args)}\`**.`
            );
          }
          return;
        case "/think": {
          const normalized = args.toLowerCase();
          if (!normalized) {
            const label =
              chat.thinkEnabled === null
                ? "using the model's default"
                : chat.thinkEnabled
                  ? "on"
                  : "off";
            chat.appendLocalAssistantMessage(
              `Thinking is currently **${label}**. Use \`/think on\` or \`/think off\` to change it.`
            );
          } else if (normalized === "on" || normalized === "true") {
            chat.setThinkEnabled(true);
            chat.appendLocalAssistantMessage(
              "Thinking **on** — I'll take a moment to reason before replying."
            );
          } else if (normalized === "off" || normalized === "false") {
            chat.setThinkEnabled(false);
            chat.appendLocalAssistantMessage(
              "Thinking **off** — I'll reply directly without a reasoning pass."
            );
          } else {
            chat.appendLocalAssistantMessage(
              `Didn't recognise \`${escapeMarkdown(args)}\`. Use \`/think on\` or \`/think off\`.`
            );
          }
          return;
        }
        case "/help":
          chat.appendLocalAssistantMessage(buildHelpMarkdown(commands));
          return;
        default:
          return;
      }
    },
    [chat, adapter.chatModels, commands, showModels]
  );

  const handleRetry = useCallback(() => {
    chat.clearError();
    void chat.resendLastUserMessage();
  }, [chat]);

  const empty = chat.messages.length === 0 && !chat.sending && !chat.messagesLoading;

  return (
    // `data-ruby` scopes ruby-ui's theme variables (`ensureRubyTheme`) to
    // this subtree, exactly as ruby-ui's `RubyWidget` wraps its own.
    <section
      data-ruby=""
      aria-label="Ruby assistant"
      className="flex h-full min-h-0 flex-col overflow-hidden border-r border-[color:var(--border)] bg-[color:var(--bg-raised)]"
    >
      <RubyHeader
        status={status}
        chatModels={adapter.chatModels}
        onNewChat={chat.newChat}
        onToggleHistory={() => setHistoryOpen((v) => !v)}
        onToggleMinimize={closePanel}
        onClose={closePanel}
        historyOpen={historyOpen}
        minimized={false}
        unreadCount={0}
        model={chat.model}
        thinkEnabled={chat.thinkEnabled}
        onModelClick={showModels}
      />
      {/* `relative`: the history drawer is positioned over this body. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[color:var(--bg)]">
        {chat.error ? (
          <div
            role="alert"
            className="flex items-center gap-2 border-b border-[color:var(--border)] bg-[color:var(--danger-bg)] px-3 py-1.5 text-xs text-[color:var(--danger)]"
          >
            <span className="min-w-0 flex-1">{chat.error}</span>
            {chat.messages.some((m) => m.role === "user") ? (
              <button
                type="button"
                onClick={handleRetry}
                disabled={chat.sending}
                className="shrink-0 rounded border border-[color:var(--danger)] px-2 py-0.5 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              >
                Retry
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Dismiss error"
              onClick={chat.clearError}
              className="shrink-0 px-1 text-sm leading-none"
            >
              ×
            </button>
          </div>
        ) : null}
        {empty ? (
          <RubyEmptyState
            greeting={adapter.greeting}
            starterChips={adapter.starterChips}
            currentPath={currentPath}
            onChipClick={sendPrompt}
          />
        ) : (
          <RubyMessageList
            messages={chat.messages}
            typing={chat.sending || chat.messagesLoading}
            sessionId={chat.activeSessionId}
            onFollowUpClick={sendPrompt}
            onRegenerate={(messageId) => void chat.regenerateTurn(messageId)}
          />
        )}
        <RubyHistoryDrawer
          open={historyOpen}
          storageKeyPrefix={adapter.storageKeyPrefix}
          userId={userId}
          sessions={chat.sessions}
          activeSessionId={chat.activeSessionId}
          loading={chat.listLoading}
          error={chat.listError}
          onSelect={chat.setActiveSession}
          onNewChat={chat.newChat}
          onRename={(id, title) => void chat.renameSession(id, title)}
          onDelete={(id) => void chat.deleteSession(id)}
          onDeleteMultiple={(ids) => void chat.deleteSessions(ids)}
          onClose={() => setHistoryOpen(false)}
        />
      </div>
      <RubyInput
        ref={inputRef}
        value={input}
        onChange={setInput}
        onSubmit={submit}
        onSlashCommand={handleSlashCommand}
        disabled={chat.sending}
      />
    </section>
  );
}
